import type { MutableRefObject, ReactNode } from 'react';
import type { FieldType, GetFieldTypeParams } from '/react4xp/components/react-hook-form/getFieldType';
import type {
	FieldValues,
	UseFormReturn,
} from 'react-hook-form';
import type {
	ZodObject,
	ZodRawShape,
} from 'zod';

import React, { createContext, useContext } from 'react';

// When one or more field(s) fail validation, a list of links to the invalid fields are displayed in
// the ErrorSummary at the bottom of the form.
//
// Clicking a link has two behaviours:
// 1. If the field has a single input
//    1.1 Its label is scolled into view, top aligned.
//    1.2 Focus is set inside the input field (if possible)
// 2. If the field has multiple inputs
//    2.1 Its label is scolled into view, top aligned.
//    2.2 Focus is set to the label so that tabbing sets focus to the first input.
//
// Scrolling should be consistent across browsers.
// To achive this behaviour we need to keep two Records. One for scrolling, and one for focus.
// We can't use react-hook-form builtin-ref since it's not a real ref, so the element is not
// available for the scrolling, neither does it's focus method support preventScroll.

export type SetErrorSummaryLabel = (params: {field: string, label: string}) => void;
export type ErrorSummaryLabels = Record<string, string>;

export type FocusRef = HTMLElement;
export type ScrollRef = HTMLElement;
export type FocusRefs = Record<string, FocusRef>;
export type ScrollRefs = Record<string, ScrollRef>;
export type SetFocusRef = (params: { field: string, el: FocusRef }) => void;
export type SetScrollRef = (params: { field: string, el: ScrollRef }) => void;

type FormContext<
	TFieldValues extends FieldValues,
	TContext = unknown,
	TransformedValues extends FieldValues | undefined = undefined,
> = UseFormReturn<TFieldValues, TContext, TransformedValues> & {
	defaultValues: TFieldValues;
	emptyValues: TFieldValues;
	errorSummaryLabels: ErrorSummaryLabels;
	focusRefs: FocusRefs;
	getRequired: (path: string) => boolean;
	setErrorSummaryLabel: SetErrorSummaryLabel;
	setFocusRef: SetFocusRef;
	setScrollRef: SetScrollRef;
	scrollRefs: ScrollRefs;
	zodSchema: ZodObject<ZodRawShape>;
	getFieldType: (params: {fieldName: GetFieldTypeParams['fieldName'];}) => FieldType;
};

export const FormContext = createContext({});


export const FormProvider = ({ children, value }: {
	children?: ReactNode
	value: object
}): React.JSX.Element => {
	return (
		<FormContext.Provider value={value}>
			{children}
		</FormContext.Provider>
	);
};

export const useFormContext = <
	TFieldValues extends FieldValues,
	TContext = unknown,
	TransformedValues extends FieldValues | undefined = undefined,
>(): FormContext<TFieldValues, TContext, TransformedValues> => {
	const context = useContext(FormContext) as FormContext<TFieldValues, TContext, TransformedValues>;

	if (!context) {
		throw new Error('useFormContext must be used within a FormProvider');
	}

	return context;
};
